import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import supabase from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';
import AccessRequestForm from '../components/auth/AccessRequestForm';
import PlatformAdminForm from '../components/auth/PlatformAdminForm';
import PendingApprovalScreen from '../components/auth/PendingApprovalScreen';
import { useAccessRequest } from '../hooks/useAccessRequest';
import { usePlatformAdminRequest } from '../hooks/usePlatformAdminRequest';
import logger from '../lib/logger';
import { checkLoginLimit, checkSignupLimit, formatResetTime } from '../lib/rateLimit';

const {
  FiMail, FiLock, FiEye, FiEyeOff, FiArrowLeft,
  FiUser, FiAlertCircle, FiLoader, FiCheckCircle,
  FiShield, FiClock, FiInfo,
} = FiIcons;

// ─────────────────────────────────────────────────────────────────────────────
// Comprehensive Supabase auth error sanitiser — covers all known
// error strings from @supabase/auth-js v2.x plus network failures.
// ─────────────────────────────────────────────────────────────────────────────
const sanitizeAuthError = (raw = '', mode = 'login') => {
  const msg = (raw || '').toLowerCase();

  if (!msg) {
    return mode === 'signup'
      ? 'Could not create account. Please try again.'
      : 'Sign in failed. Please try again.';
  }

  // Wrong credentials
  if (
    msg.includes('invalid login credentials') ||
    msg.includes('invalid credentials') ||
    msg.includes('invalid login') ||
    msg.includes('user not found') ||
    msg.includes('no user found') ||
    msg.includes('wrong password') ||
    msg.includes('incorrect password')
  ) return 'Incorrect email or password. Please try again.';

  // Email not confirmed
  if (
    msg.includes('email not confirmed') ||
    msg.includes('email link is invalid or has expired') ||
    msg.includes('token has expired') ||
    msg.includes('confirmation token')
  ) return 'Please confirm your email address before signing in. Check your inbox.';

  // Account exists
  if (
    msg.includes('user already registered') ||
    msg.includes('already registered') ||
    msg.includes('already exists') ||
    msg.includes('duplicate')
  ) return 'An account with this email already exists. Try signing in instead.';

  // Signup disabled
  if (
    msg.includes('signup is disabled') ||
    msg.includes('signups not allowed') ||
    msg.includes('email signups are disabled') ||
    msg.includes('new user signups')
  ) return 'New sign-ups are currently closed. Please apply for access below.';

  // Email provider disabled
  if (
    msg.includes('email logins are disabled') ||
    msg.includes('provider is not enabled') ||
    msg.includes('email provider is not enabled')
  ) return 'Email sign-in is not available right now. Please contact support.';

  // Password requirements
  if (
    msg.includes('password should be') ||
    msg.includes('password must be') ||
    msg.includes('password is too short') ||
    msg.includes('weak password') ||
    (msg.includes('password') && msg.includes('character'))
  ) return 'Password must be at least 8 characters long.';

  // Invalid email
  if (
    msg.includes('invalid email') ||
    msg.includes('email address is invalid') ||
    msg.includes('unable to validate email')
  ) return 'Please enter a valid email address.';

  // Rate limited
  if (
    msg.includes('rate limit') ||
    msg.includes('too many requests') ||
    msg.includes('too many attempts') ||
    msg.includes('for security purposes') ||
    msg.includes('email rate limit exceeded')
  ) return 'Too many attempts. Please wait a moment before trying again.';

  // Network failures
  if (
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed') ||
    msg.includes('connection') ||
    msg.includes('timeout') ||
    msg.includes('econnrefused') ||
    msg.includes('networkerror')
  ) return 'Unable to connect. Please check your internet connection and try again.';

  // Server / DB errors
  if (
    msg.includes('database error') ||
    msg.includes('unexpected error') ||
    msg.includes('internal server error') ||
    msg.includes('service unavailable') ||
    msg.includes('bad gateway') ||
    msg.includes('503') ||
    msg.includes('502')
  ) return 'A server error occurred. Please try again in a moment.';

  // Session / token errors
  if (
    msg.includes('session expired') ||
    msg.includes('refresh token') ||
    msg.includes('jwt expired') ||
    msg.includes('token is expired')
  ) return 'Your session has expired. Please sign in again.';

  // Missing env / config (dev-only scenario — supabase.js should catch first)
  if (msg.includes('missing') && msg.includes('supabase')) {
    return 'App configuration error. Please contact support.';
  }

  // Mode-aware fallback
  return mode === 'signup'
    ? 'Could not create account. Please check your details and try again.'
    : 'Incorrect email or password. Please try again.';
};

// ─────────────────────────────────────────────────────────────────────────────
// Dev-only: show a small debug banner with the raw Supabase error
// so developers can diagnose issues without shipping raw errors to users.
// ─────────────────────────────────────────────────────────────────────────────
const IS_DEV = import.meta.env.DEV;

const DevErrorBanner = ({ rawError }) => {
  if (!IS_DEV || !rawError) return null;
  return (
    <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl mt-2">
      <SafeIcon icon={FiInfo} className="text-yellow-400 text-xs flex-shrink-0 mt-0.5" />
      <p className="text-[10px] font-mono text-yellow-400 break-all">
        <span className="font-bold uppercase tracking-widest">DEV: </span>
        {rawError}
      </p>
    </div>
  );
};

const AuthPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, profile, loading, profileLoading } = useAuth();

  const [mode,              setMode]              = useState('login');
  const [email,             setEmail]             = useState('');
  const [password,          setPassword]          = useState('');
  const [showPw,            setShowPw]            = useState(false);
  const [authError,         setAuthError]         = useState('');
  const [rawDevError,       setRawDevError]       = useState('');
  const [submitting,        setSubmitting]        = useState(false);
  const [showAccessForm,    setShowAccessForm]    = useState(false);
  const [accessType,        setAccessType]        = useState('creator');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const [creatorFormSubmitting, setCreatorFormSubmitting] = useState(false);
  const [creatorFormError,      setCreatorFormError]      = useState('');
  const [adminFormSubmitting,   setAdminFormSubmitting]   = useState(false);
  const [adminFormError,        setAdminFormError]        = useState('');

  const returnToRef = useRef(location.state?.returnTo || null);

  const {
    request: creatorRequest,
    submitRequest: submitCreatorRequest,
    loading: creatorRequestLoading,
  } = useAccessRequest(user?.id);

  const {
    request: adminRequest,
    submitRequest: submitAdminRequest,
    loading: adminRequestLoading,
  } = usePlatformAdminRequest(user?.id);

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!user) return;
    if (creatorRequestLoading || adminRequestLoading) return;

    const role = profile?.role || 'fan';
    const activeRequest = creatorRequest || adminRequest;
    const hasPendingOrReviewed =
      activeRequest && ['pending', 'approved', 'rejected'].includes(activeRequest.status);
    if (hasPendingOrReviewed) return;

    const returnTo = returnToRef.current;
    if (returnTo) {
      returnToRef.current = null;
      navigate(returnTo, { replace: true });
      return;
    }

    if (role === 'platform_admin') { navigate('/admin',       { replace: true }); return; }
    if (role === 'label_admin')    { navigate('/label-admin', { replace: true }); return; }
    if (role === 'artist')         { navigate('/dashboard',   { replace: true }); return; }
    navigate('/', { replace: true });
  }, [
    user, profile, loading, profileLoading,
    creatorRequestLoading, adminRequestLoading,
    creatorRequest, adminRequest, navigate,
  ]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  const activeRequest = creatorRequest || adminRequest;
  const hasPendingOrReviewed =
    user && !profileLoading && !creatorRequestLoading && !adminRequestLoading &&
    activeRequest && ['pending', 'approved', 'rejected'].includes(activeRequest.status);

  if (hasPendingOrReviewed) {
    return <PendingApprovalScreen request={activeRequest} onSignOut={handleSignOut} />;
  }

  if (submissionSuccess) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6 relative overflow-hidden">
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="ambient-orb-pink w-[600px] h-[600px] top-[-20%] right-[-15%] animate-caustic" />
          <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-15%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
        </div>
        <div className="relative z-10 w-full max-w-md space-y-6">
          <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group w-fit">
            <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center">
              <SafeIcon icon={FiArrowLeft} className="text-sm" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest">Home</span>
          </Link>
          <div className="glass-strong rounded-[2.5rem] p-10 space-y-6 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mx-auto">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-400" />
            </div>
            <div className="space-y-3">
              <h1 className="text-2xl font-black uppercase tracking-tighter text-white">Application Submitted!</h1>
              <p className="text-zinc-500 font-bold text-sm">
                {accessType === 'platform_admin'
                  ? 'Thank you for applying to join the Vision Direct leadership team. We will review your application within 5–7 business days.'
                  : 'Thank you for applying to Vision Direct. Our team will review your application within 3–5 business days.'}
              </p>
              <p className="text-zinc-600 font-bold text-xs">Please check your email for updates on your application status.</p>
            </div>
            <button
              onClick={() => { setSubmissionSuccess(false); setShowAccessForm(false); setAccessType('creator'); setMode('login'); }}
              className="w-full py-4 btn-brand rounded-2xl text-xs font-black uppercase tracking-widest">
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setRawDevError('');

    const limitCheck = mode === 'login' ? checkLoginLimit() : checkSignupLimit();
    if (!limitCheck.allowed) {
      setAuthError(`Too many attempts. Please wait ${formatResetTime(limitCheck.resetIn)} before trying again.`);
      return;
    }

    setSubmitting(true);
    try {
      let result;
      if (mode === 'login') {
        result = await supabase.auth.signInWithPassword({ email, password });
      } else {
        result = await supabase.auth.signUp({ email, password });
      }

      if (result.error) {
        const rawMsg = result.error.message || '';
        setRawDevError(`[${result.error.status}] ${result.error.name}: ${rawMsg}`);
        setAuthError(sanitizeAuthError(rawMsg, mode));
        logger.error('[AuthPage] auth error', {
          status: result.error.status,
          name:   result.error.name,
        });
      }
    } catch (err) {
      const rawMsg = err?.message || 'Unknown exception';
      setRawDevError(`Exception: ${rawMsg}`);
      setAuthError(sanitizeAuthError(rawMsg, mode));
      logger.error('[AuthPage] auth exception', { message: rawMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatorSubmit = async (payload) => {
    setCreatorFormError('');
    setCreatorFormSubmitting(true);
    try {
      const { error } = await submitCreatorRequest(payload);
      if (error) {
        setCreatorFormError(error.message || 'Failed to submit request. Please try again.');
      } else {
        setAccessType('creator');
        setSubmissionSuccess(true);
        setShowAccessForm(false);
      }
    } catch (err) {
      setCreatorFormError(err.message || 'An unexpected error occurred.');
    } finally {
      setCreatorFormSubmitting(false);
    }
  };

  const handleAdminSubmit = async (payload) => {
    setAdminFormError('');
    setAdminFormSubmitting(true);
    try {
      const { error } = await submitAdminRequest(payload);
      if (error) {
        setAdminFormError(error.message || 'Failed to submit request. Please try again.');
      } else {
        setAccessType('platform_admin');
        setSubmissionSuccess(true);
        setShowAccessForm(false);
      }
    } catch (err) {
      setAdminFormError(err.message || 'An unexpected error occurred.');
    } finally {
      setAdminFormSubmitting(false);
    }
  };

  const userRole          = profile?.role || 'fan';
  const isArtistOrLabel   = userRole === 'artist' || userRole === 'label_admin';
  const isPlatformAdmin   = userRole === 'platform_admin';
  const hasCreatorRequest = !!creatorRequest;
  const hasAdminRequest   = !!adminRequest;
  const showCreatorApply  = !isArtistOrLabel && !isPlatformAdmin && !hasCreatorRequest;
  const showAdminApply    = !isPlatformAdmin && !hasAdminRequest;

  if (showAccessForm) {
    if (accessType === 'platform_admin') {
      return (
        <PlatformAdminForm
          user={user}
          onSubmit={handleAdminSubmit}
          loading={adminFormSubmitting || adminRequestLoading}
          error={adminFormError}
        />
      );
    }
    return (
      <AccessRequestForm
        user={user}
        onSubmit={handleCreatorSubmit}
        loading={creatorFormSubmitting || creatorRequestLoading}
        error={creatorFormError}
      />
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[600px] h-[600px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-15%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group w-fit">
          <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
            <SafeIcon icon={FiArrowLeft} className="text-sm" />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Home</span>
        </Link>

        <div className="glass-strong rounded-[2.5rem] p-10 space-y-8">
          <div className="text-center space-y-3">
            <img
              src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
              alt="Vision Direct"
              className="h-12 w-12 object-contain mx-auto drop-shadow-[0_0_12px_rgba(255,102,204,0.4)]"
            />
            <h1 className="text-3xl font-black uppercase tracking-tighter text-white">
              {mode === 'login' ? 'Welcome Back' : 'Join Vision Direct'}
            </h1>
            <p className="text-zinc-500 font-bold text-sm">
              {mode === 'login' ? 'Sign in to your account' : 'Create your account to get started'}
            </p>
          </div>

          <div className="flex gap-1 bg-zinc-900/60 border border-white/5 rounded-2xl p-1.5">
            {['login', 'signup'].map(m => (
              <button
                key={m}
                onClick={() => { setMode(m); setAuthError(''); setRawDevError(''); }}
                className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  mode === m ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
                }`}>
                {m === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Email</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Password</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder="••••••••"
                  minLength={8}
                  className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-12 text-sm font-bold text-white placeholder-zinc-700 focus:border-brand-pink/40 focus:outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-300 transition-colors">
                  <SafeIcon icon={showPw ? FiEyeOff : FiEye} className="text-sm" />
                </button>
              </div>
            </div>

            {authError && (
              <div className="flex items-start gap-3 p-4 bg-red-500/8 border border-red-500/20 rounded-2xl" role="alert">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-red-400">{authError}</p>
              </div>
            )}

            {/* Dev-only raw error — never visible in production */}
            <DevErrorBanner rawError={rawDevError} />

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-5 btn-brand rounded-2xl text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {submitting
                ? <><SafeIcon icon={FiLoader} className="animate-spin text-base" /> Processing…</>
                : mode === 'login' ? 'Sign In' : 'Create Account'
              }
            </button>
          </form>

          {(showCreatorApply || showAdminApply || hasCreatorRequest || hasAdminRequest) && (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-700">or</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              {showCreatorApply && (
                <button
                  onClick={() => { setAccessType('creator'); setShowAccessForm(true); }}
                  className="w-full py-4 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all flex items-center justify-center gap-2">
                  <SafeIcon icon={FiUser} className="text-sm" /> Apply as Creator
                </button>
              )}

              {hasCreatorRequest && !showCreatorApply && (
                <div className="w-full py-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl text-xs font-black uppercase tracking-widest text-amber-400 flex items-center justify-center gap-2 cursor-default">
                  <SafeIcon icon={FiClock} className="text-sm" />
                  Creator Application — {creatorRequest.status?.replace(/_/g, ' ')}
                </div>
              )}

              {showAdminApply && (
                <button
                  onClick={() => { setAccessType('platform_admin'); setShowAccessForm(true); }}
                  className="w-full py-4 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-2xl text-xs font-black uppercase tracking-widest text-red-400 hover:text-red-300 transition-all flex items-center justify-center gap-2">
                  <SafeIcon icon={FiShield} className="text-sm" /> Apply as Platform Admin
                </button>
              )}

              {hasAdminRequest && !showAdminApply && (
                <div className="w-full py-4 bg-red-500/5 border border-red-500/20 rounded-2xl text-xs font-black uppercase tracking-widest text-red-400/60 flex items-center justify-center gap-2 cursor-default">
                  <SafeIcon icon={FiClock} className="text-sm" />
                  Admin Application — {adminRequest.status?.replace(/_/g, ' ')}
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-[10px] font-black uppercase tracking-widest text-zinc-700">
          Vision Direct · Direct-to-Fan Platform
        </p>
      </div>
    </div>
  );
};

export default AuthPage;